import * as Yup from "yup";
import { verifyIranianNationalId } from "@persian-tools/persian-tools";

import {
  bulkSMSTypeItems,
  createTransactionTypeItems,
  discountCodeTypeItems,
  fieldTypeItems,
  invoiceStatusItems,
  orderStatusItems,
  paymentGatewayItems,
  productTypeItems,
  userRoleItems,
} from "../enum";

// Link
const linkRegex =
  /^(?!.*(?:https?:\/\/|www\.|(?:\b[a-zA-Z0-9\u0600-\u06FF-]+(?:\.[a-zA-Z\u0600-\u06FF]{2,})+\b))).*$/s;

// Phone Number
const phoneNumberRegex = /^09\d{9}$/;

export const isValidPhoneNumber = (phoneNumber) => {
  return phoneNumberRegex.test(phoneNumber);
};

// Email
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (email) => {
  return emailRegex.test(email);
};

// Text
const persianRegex = /^[\u0600-\u06FF\u200C\u200B\s]+$/;
const englishRegex = /^[A-Za-z\s]+$/;

// Date
export const isDateBiggerThan = (firstDate, secondDate) => {
  if (secondDate) {
    const convertFirstDate = new Date(firstDate);
    const convertSecondDate = new Date(secondDate);

    return convertSecondDate.getTime() < convertFirstDate.getTime();
  } else {
    const dateNow = new Date();
    const convertFirstDate = new Date(firstDate);

    return dateNow.getTime() < convertFirstDate.getTime();
  }
};

// Validation
const phoneNumber = Yup.string()
  .required("شماره موبایل الزامی است")
  .min(11, "شماره موبایل نمی تواند کمتر از 11 کاراکتر باشد")
  .max(11, "شماره موبایل نمی تواند بیشتر از 11 کاراکتر باشد")
  .matches(phoneNumberRegex, "شماره موبایل وارد شده معتبر نیست");
const password = Yup.string()
  .required("کلمه عبور الزامی است")
  .min(6, "کلمه عبور نمی تواند کمتر از 6 کاراکتر باشد")
  .max(255, "کلمه عبور نمی تواند بیشتر از 255 کاراکتر باشد");
const newPassword = Yup.string()
  .required("کلمه عبور جدید الزامی است")
  .min(6, "کلمه عبور جدید نمی تواند کمتر از 6 کاراکتر باشد")
  .max(255, "کلمه عبور جدید نمی تواند بیشتر از 255 کاراکتر باشد");
const confirmPassword = Yup.string()
  .required("تکرار کلمه عبور الزامی است")
  .oneOf(
    [Yup.ref("password"), null],
    "کلمه عبور و تکرار کلمه عبور یکسان نیستند"
  );
const firstName = Yup.string()
  .required("نام الزامی است")
  .max(255, "نام نمی تواند بیشتر از 255 کاراکتر باشد")
  .matches(persianRegex, "نام باید حروف فارسی باشد")
  .trim();
const lastName = Yup.string()
  .required("نام خانوادگی الزامی است")
  .max(255, "نام خانوادگی نمی تواند بیشتر از 255 کاراکتر باشد")
  .matches(persianRegex, "نام خانوادگی باید حروف فارسی باشد")
  .trim();
const email = Yup.string()
  .required("پست الکترونیک الزامی است")
  .email("پست الکترونیک وارد شده معتبر نیست")
  .trim();
const nationalNumber = Yup.string()
  .required("کد ملی الزامی است")
  .min(10, "کدملی نمی تواند کمتر از 10 کاراکتر باشد")
  .max(10, "کدملی نمی تواند بیشتر از 10 کاراکتر باشد")
  .test("is-valid-national-code", "کد ملی وارد شده معتبر نیست", (value) =>
    verifyIranianNationalId(value || "")
  );
const avatar = Yup.string()
  .max(255, "نام فایل تصویر پروفایل نمی تواند بیشتر از 255 کاراکتر باشد")
  .nullable();
const role = Yup.string()
  .required("نقش الزامی است")
  .oneOf(userRoleItems, "نقش مجاز نیست");
const verifyCode = Yup.string()
  .required("کد تایید الزامی است")
  .min(6, "کد تایید نمی تواند کمتر از 6 کاراکتر باشد")
  .max(6, "کد تایید نمی تواند بیشتر از 6 کاراکتر باشد");
const permissions = Yup.string()
  .required("سطح دسترسی الزامی است")
  .min(16, "سطح دسترسی نمی تواند کمتر از 16 کاراکتر باشد")
  .max(16, "سطح دسترسی نمی تواند بیشتر از 16 کاراکتر باشد");
const geoName = Yup.string()
  .required("نام الزامی است")
  .max(255, "نام نمی تواند بیشتر از 255 کاراکتر باشد")
  .matches(persianRegex, "نام باید حروف فارسی باشد")
  .trim();
const description = Yup.string().max(
  4096,
  "توضیحات نمی تواند بیشتر از 4096 کاراکتر باشد"
);
const address = Yup.string().max(
  1024,
  "توضیحات نمی تواند بیشتر از 1024 کاراکتر باشد"
);
const title = Yup.string()
  .required("عنوان الزامی است")
  .max(255, "عنوان نمی تواند بیشتر از 255 کاراکتر باشد");
const message = Yup.string()
  .required("متن پیام الزامی است")
  .max(4096, "متن پیام نمی تواند بیشتر از 4096 کاراکتر باشد");
const amount = Yup.string()
  .required("مبلغ الزامی است")
  .min(0, "مبلغ کوچکتر از 0 است");
const transactionType = Yup.string()
  .required("نوع تراکنش الزامی است")
  .oneOf(createTransactionTypeItems, "نوع تراکنش مجاز نیست");
const otp = Yup.string()
  .required("کداعتبارسنجی الزامی است")
  .min(5, "کداعتبارسنجی نمی تواند کمتر از 5 کاراکتر باشد")
  .max(5, "کداعتبارسنجی نمی تواند بیشتر از 5 کاراکتر باشد");
const isRegisterStaff = Yup.boolean().required(
  "وضعیت ثبت نام همکاران الزامی است"
);
const invoiceExpiration = Yup.string().required("مدت انقضا فاکتور الزامی است");
const isRequired = Yup.boolean().required("وضعیت اجباری بودن فیلد الزامی است");
const fieldName = Yup.string()
  .required("نام فیلد الزامی است")
  .matches(englishRegex, "نام فیلد باید حروف انگلیسی باشد");
const label = Yup.string().required("لیبل الزامی است");
const code = Yup.string().required("کد الزامی است");
const price = Yup.string().required("قیمت الزامی است");
const taxPercent = Yup.string().required("درصد مالیات الزامی است");
const renewalPeriod = Yup.string().required("دوره تمدید الزامی است");
const fieldType = Yup.string()
  .required("نوع فیلد الزامی است")
  .oneOf(fieldTypeItems, "نوع فیلد مجاز نیست");
const productType = Yup.string()
  .required("نوع محصول الزامی است")
  .oneOf(productTypeItems, "نوع محصول مجاز نیست");
const fieldOptions = Yup.array(
  Yup.object({
    value: label,
    label: label,
  })
).when("type", {
  is: (type) => [fieldTypeItems[6], fieldTypeItems[7]].includes(type),
  then: (schema) =>
    schema
      .min(1, "حداقل یک گزینه برای نوع انتخابی مورد نیاز است")
      .required("گزینه‌ها الزامی هستند"),
  otherwise: (schema) => schema.notRequired(),
});
const picture = Yup.string()
  .max(255, "نام فایل تصویر نمی تواند بیشتر از 255 کاراکتر باشد")
  .nullable();
const id = Yup.string().required("شناسه الزامی است");
const user = Yup.object({
  value: id,
  label: id,
}).required("کاربر الزامی است");
const userId = Yup.string().required("شناسه کاربر الزامی است");
const assign = Yup.array(
  Yup.object({
    value: id,
    label: id,
  }).required("کاربر الزامی است")
);
const product = Yup.object({
  value: id,
  label: id,
}).required("محصول الزامی است");
const category = Yup.object({
  value: id,
  label: id,
}).required("دسته بندی الزامی است");
const subCategory = Yup.object({
  value: Yup.string().nullable(),
  label: Yup.string().nullable(),
}).nullable();
const fields = Yup.array(
  Yup.object({
    value: id,
    label: id,
  }).required("فیلد الزامی است")
)
  .min(1, "حداقل 1 فیلد الزامی است")
  .required("فیلد الزامی است");
const orderStatus = Yup.string()
  .oneOf(orderStatusItems, "وضعیت مجاز نیست")
  .required("وضعیت الزامی است");
const details = Yup.object({
  [Yup.string().required("نام جزئیات الزامی است")]: Yup.string(),
}).required("جزئیات سفارش الزامی است");
const discountCodeType = Yup.string()
  .oneOf(discountCodeTypeItems, "نوع کد تخفیف مجاز نیست")
  .required("نوع کد تخفیف الزامی است");
const percentage = Yup.number()
  .min(0, "درصد تخفیف کوچکتر از 0 است")
  .max(100, "درصد بزرگتر از 100 است")
  .required("درصد تخفیف الزامی است");
const minPurchase = Yup.number()
  .min(0, "حداقل مبلغ خرید کوچکتر از 0 است")
  .required("حداقل مبلغ خرید الزامی است");
const maxDiscount = Yup.number()
  .min(0, "حداکثر مبلغ تخفیف کوچکتر از 0 است")
  .required("حداکثر مبلغ تخفیف الزامی است");
const limit = Yup.string().required("تعداد کد تخفیف الزامی است");
const singleUse = Yup.boolean().required("محدودیت استفاده کاربر الزامی است");
const invoiceStatus = Yup.string()
  .oneOf(invoiceStatusItems, "وضعیت فاکتور مجاز نیست")
  .required("وضعیت فاکتور الزامی است");
const document = Yup.object({
  title: Yup.string().required("عنوان مستند الزامی است"),
  path: Yup.string().required("مسیر مستند الزامی است"),
}).required("مستند الزامی است");
const documents = Yup.array(document).nullable();
const expiredAt = Yup.string().required("تاریخ انقضا الزامی است");
const question = Yup.string()
  .required("سوال الزامی است")
  .max(1024, "سوال نمی تواند بیشتر از 1024 کاراکتر باشد");
const answer = Yup.string()
  .required("پاسخ الزامی است")
  .max(4096, "پاسخ نمی تواند بیشتر از 4096 کاراکتر باشد");
const questions = Yup.array(Yup.object({ question, answer }))
  .required("سوالات الزامی است")
  .min(1, "حداقل یک سوال الزامی است");
const terms = Yup.string().nullable();
const staffTerms = Yup.string().nullable();
const aboutUs = Yup.string().nullable();
const isTerms = Yup.boolean()
  .oneOf([true], "پذیرفتن قوانین و مقررات الزامی است")
  .required("پذیرفتن قوانین و مقررات الزامی است");
const bulkMessage = Yup.string()
  .required("پیامک الزامی است")
  .max(512, "پیامک نمی‌تواند بیشتر از 512 کاراکتر باشد")
  .matches(linkRegex, "پیامک نمی‌تواند شامل لینک باشد")
  .trim();
const bulkSMSType = Yup.string()
  .oneOf(bulkSMSTypeItems, "نوع مجاز نیست")
  .required("نوع الزامی است");
const gateway = Yup.string()
  .oneOf(paymentGatewayItems, "نوع درگاه پرداخت مجاز نیست")
  .required("نوع درگاه پرداخت الزامی است");
const users = Yup.array(user).nullable();
const renewalDate = Yup.string().nullable();
const paymentGatewayIsActive = Yup.boolean().required(
  "وضعیت درگاه پرداخت الزامی است"
);
const paymentGatewayBaseUrl = Yup.string().when(
  "isActive",
  (isActive, schema) => {
    return isActive[0]
      ? schema
          .required("آدرس وب سرویس‌ الزامی است")
          .url("آدرس وب سرویس‌ معتبر وارد کنید")
      : schema;
  }
);
const paymentGatewayCallbackUrl = Yup.string().when(
  "isActive",
  (isActive, schema) =>
    isActive[0]
      ? schema
          .required("آدرس بازگشت مشتری الزامی است")
          .url("آدرس بازگشت مشتری معتبر وارد کنید")
      : schema
);
const merchantId = Yup.string().when("isActive", (isActive, schema) =>
  isActive[0]
    ? schema
        .required("کد درگاه پرداخت الزامی است")
        .min(15, "کد درگاه پرداخت نمی‌تواند کمتر از 15 کاراکتر باشد")
    : schema
);
const zarinpal = Yup.object().shape({
  isActive: paymentGatewayIsActive,
  baseUrl: paymentGatewayBaseUrl,
  merchantId,
  callbackUrl: paymentGatewayCallbackUrl,
  description,
});
const paymentGateway = Yup.object().shape({
  zarinpal,
});
const notification = Yup.object()
  .shape({
    push: Yup.boolean().required("وضعیت اعلان داخلی الزامی است"),
    email: Yup.boolean().required("وضعیت ارسال پست الکترونیک الزامی است"),
    sms: Yup.boolean().required("وضعیت ارسال پیامک الزامی است"),
  })
  .required("اعلان الزامی است");
const notifications = Yup.object()
  .shape({
    register: notification,
    order: notification,
    payment: notification,
    changeOrderStatus: notification,
    newTicket: notification,
    replyTicket: notification,
    changeTicketStatus: notification,
    bulkSMS: notification,
  })
  .required("اعلان الزامی است");

// Login User
export const loginUserValidation = Yup.object({
  nationalNumber,
  password,
});

// Register User
export const registerUserValidation = Yup.object({
  firstName,
  lastName,
  nationalNumber,
  phoneNumber,
  email,
  address,
  password,
  confirmPassword,
  isTerms,
});

// Create User
export const confirmRegisterValidation = Yup.object({
  verifyCode,
});

// Forgot Password
export const forgotPasswordValidation = Yup.object({
  nationalNumber,
  phoneNumber,
});

// Edit User
export const editUserValidation = Yup.object({
  avatar: avatar.nullable(),
  firstName,
  lastName,
  phoneNumber,
  email,
  address,
});

// Change Password
export const changePasswordValidation = Yup.object({
  password,
  newPassword,
  confirmPassword: Yup.string()
    .required("تکرار کلمه عبور جدید الزامی است")
    .oneOf(
      [Yup.ref("newPassword"), null],
      "کلمه عبور جدید و تکرار کلمه عبور جدید یکسان نیستند"
    ),
});

// Create User For Admin
export const createUserForAdminValidation = Yup.object().shape({
  firstName,
  lastName,
  nationalNumber,
  phoneNumber,
  email,
  address,
  role,
  permissions,
  password,
  confirmPassword,
});

// Edit User For Admin
export const editUserForAdminValidation = Yup.object().shape({
  firstName,
  lastName,
  email,
  address,
  role,
  nationalNumber,
  phoneNumber,
  permissions,
});

// Change User Password
export const changeUserPasswordValidation = Yup.object().shape({
  password,
  confirmPassword,
});

// Create Geo
export const createGeoValidation = Yup.object({
  name: geoName,
  provinceId: Yup.string().nullable(),
});

// Create Category
export const createCategoryValidation = Yup.object({
  avatar,
  title,
  description,
  categoryId: Yup.string().nullable(),
});

// Create Ticket
export const createTicketValidation = Yup.object().shape({
  title,
  message,
  product: Yup.string().nullable(),
});

// Create Ticket For Admin
export const createTicketForAdminValidation = Yup.object().shape({
  user: userId,
  title,
  message,
  product: Yup.string().nullable(),
});

// Assign Ticket For Admin
export const assignTicketForAdminValidation = Yup.object().shape({
  assign,
});

// Create Transaction
export const createTransactionValidation = Yup.object().shape({
  amount,
  documents,
  description,
  type: transactionType,
});

// Edit Transaction
export const editTransactionValidation = Yup.object().shape({
  documents,
  description,
});

// Send Phone Number OTP
export const sendPhoneNumberOTPValidation = Yup.object({
  phoneNumber,
});

// Verify Phone Number OTP
export const verifyPhoneNumberOTPValidation = Yup.object({
  phoneNumber,
  otp,
});

// Send Email OTP
export const sendEmailOTPValidation = Yup.object({
  email,
});

// Verify Email OTP
export const verifyEmailOTPValidation = Yup.object({
  email,
  otp,
});

// Edit Setting
export const editSettingValidation = Yup.object().shape({
  isRegisterStaff,
  invoiceExpiration,
  terms,
  staffTerms,
  aboutUs,
  taxPercent,
  paymentGateway,
  notifications,
});

// Create Field
export const createFieldValidation = Yup.object().shape({
  name: fieldName,
  label,
  type: fieldType,
  options: fieldOptions,
  isRequired,
});

// Create Product
export const createProductValidation = Yup.object().shape({
  picture,
  title,
  code,
  category,
  subCategory,
  type: productType,
  price,
  renewalPeriod,
  fields,
  description,
});

// Create Order
export const createOrderValidation = details;

// Edit Order
export const editOrderValidation = Yup.object().shape({
  renewalDate,
  status: orderStatus,
});

// Create Discount Code
export const createDiscountCodeValidation = Yup.object().shape({
  title,
  code,
  type: discountCodeType,
  percentage,
  amount,
  minPurchase,
  maxDiscount,
  products: Yup.array(product).nullable(),
  categories: Yup.array(category).nullable(),
  singleUse,
  limit,
  users: Yup.array(user).nullable(),
  expiredAt: Yup.string().nullable(),
  description,
});

// Create Invoice
export const createInvoiceValidation = Yup.object().shape({
  amount,
  status: invoiceStatus,
  description,
  expiredAt,
  documents,
  user,
});

// Create FAQ
export const createFAQValidation = Yup.object().shape({
  title,
  questions,
});

// Send Bulk SMS
export const createBulkSMSValidation = Yup.object().shape({
  title,
  message: bulkMessage,
  type: bulkSMSType,
  description,
  users,
});

// Edit Bulk SMS
export const editBulkSMSValidation = Yup.object().shape({
  title,
  description,
});

// Charge Wallet
export const chargeWalletValidation = Yup.object().shape({
  amount,
  gateway,
  description,
});
